// 通道健康页的接口（docs/04 第 8 节）：metrics 建好的指标快照，按通道 × 运营商给出两个窗口的比率、熔断状态与慢启动的调度 TPS（决策 D41、D48、D89）。
import { formatTime, type ChannelState, type Tone } from "@/ui";
import { api } from "./api";
import type { Channel } from "./master";

/** 一个窗口的指标，与路由策略看到的换算一致（docs/02 6.1）。 */
export interface WindowMetrics {
  delivery_rate: number; dlr_latency_ms: number; sample_size: number;
  submit_error_rate: number; submit_latency_ms: number; submit_samples: number;
}

/** limited 是熔断恢复后的慢启动，按调度 TPS 受限（决策 D89）。 */
export type CircuitState = "closed" | "half_open" | "open" | "limited";

export interface ChannelHealth {
  channel_id: number; mcc: string; mnc: string;
  recent: WindowMetrics; hour: WindowMetrics;
  /** 近 1 小时去掉账户影响的送达率，按质量打分用它（决策 D97）；samples 是参与计算的可比样本数。没有可比样本时不出现。 */
  adjusted?: { delivery_rate: number; samples: number };
  circuit: CircuitState; circuit_since?: string;
  /** 慢启动受限时的调度 TPS；tps_actual 是上一个整分钟的实际 TPS。 */
  tps_limit?: number; tps_actual: number;
}

export interface ChannelHealthList { built_at: string | null; items: ChannelHealth[] }

export const channelHealth = { list: () => api<ChannelHealthList>("/channel-health") };

/** 主备状态（决策 D92）：primary 主用、verifying 验证中、switched 切走。 */
export type PrimaryBackupState = "primary" | "verifying" | "switched";

/** 主备组在一个运营商上的状态（docs/04 第 8 节），只列与默认主用不同的：切走、验证中，以及冷却中的主用。 */
export interface PrimaryBackupRow {
  group_id: number; mcc: string; mnc: string; state: PrimaryBackupState;
  active_channel_id: number; verify_channel_ids: number[];
  since: string; verify_since?: string; quiet_until?: string;
}

// 状态的展示：切走要人注意，验证中是临时的；冷却中的主用照常走主通道，只是 10 分钟内不再验证
export const PB_STATE: Record<PrimaryBackupState, { tone: Tone; label: string }> = {
  switched: { tone: "warning", label: "已切到备用" },
  verifying: { tone: "brand", label: "验证备用中" },
  primary: { tone: "neutral", label: "主用（冷却中）" },
};

/** 一行的说明：切走时主通道在验证、验证开始的时刻、冷却到什么时候。 */
export function pbDetail(r: PrimaryBackupRow, channel: (id: number) => string): string {
  const verify = r.verify_channel_ids.map(channel).join("、");
  if (r.state === "switched" && verify) return `验证主通道是否恢复，${formatTime(r.verify_since ?? r.since, false)} 起`;
  if (r.state === "verifying") return `临时加流量验证 ${verify}`;
  return r.quiet_until ? `验证没通过，${formatTime(r.quiet_until, false)} 前不再验证` : ""; // 冷却按分钟计，与 metrics 一致
}

/** built_at 为空表示指标快照已过期：metrics 没在运行，路由一律按主用走。 */
export interface PrimaryBackupList { built_at: string | null; items: PrimaryBackupRow[] }

export const primaryBackup = { list: () => api<PrimaryBackupList>("/primary-backup") };

/** 一行的状态：停用与手动摘除优先于熔断，半开（只放试探）与慢启动受限都算降级。channel 为 null 表示通道已删除，按停用显示；
 * undefined 表示通道列表还没加载完，只看熔断状态，免得加载期间整页闪成停用。 */
export function healthState(row: ChannelHealth, channel: Channel | null | undefined): ChannelState {
  if (channel === null || channel?.status === "disabled") return "disabled";
  if (channel?.manually_removed) return "removed";
  if (row.circuit === "open") return "open";
  if (row.circuit === "half_open" || row.circuit === "limited") return "degraded";
  return "healthy";
}

/** 慢启动受限的通道 id，通道配置列表据此挂"TPS 受限"标签（决策 D89）。 */
export function limitedChannels(items: ChannelHealth[] | undefined): Set<number> {
  return new Set((items ?? []).filter((r) => r.circuit === "limited").map((r) => r.channel_id));
}

/** 调度 TPS：受限时显示"限额 / 实际"，否则只显示实际。 */
export function formatTPS(row: ChannelHealth): string {
  const actual = row.tps_actual.toFixed(1);
  return row.circuit === "limited" ? `${row.tps_limit ?? 0} / ${actual}` : actual;
}

/** 比率显示成百分数；没有样本时显示横线，不显示 0%。 */
/** 修正送达率的悬停说明（决策 D97）：没有值时说明为什么，样本不足 50 时说明打分还不用它。 */
export function adjustedHint(a: ChannelHealth["adjusted"]): string {
  if (!a) return "没有可比样本：同一账户要在这个运营商上至少两条通道各有 30 条终态结果才能比较";
  return a.samples < 50 ? `可比样本 ${a.samples}，不足 50，按质量打分暂不用它` : `可比样本 ${a.samples}`;
}

export function formatRate(rate: number, samples: number): string {
  return samples > 0 ? `${(rate * 100).toFixed(1)}%` : "—";
}
